import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Plus, Edit2, Trash2, Check, X, Users, MessageSquare, 
  BarChart3, Star, Upload, Save, Image, Eye, TrendingUp, Calendar
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

import API from '../utils/api';

const AdminPanel = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [editingCreator, setEditingCreator] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creatorSearch, setCreatorSearch] = useState('');
  const [selectedCreatorAnalytics, setSelectedCreatorAnalytics] = useState(null);
  const [isCreatorAnalyticsOpen, setIsCreatorAnalyticsOpen] = useState(false);

  // Form state for creator
  const [creatorForm, setCreatorForm] = useState({
    name: '',
    bio: '',
    affiliate_link: '',
    profile_image: '',
    cover_image: '',
    is_featured: false,
    is_top_month: false,
    photos_count: 0,
    videos_count: 0,
    price: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, creatorsRes, commentsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/creators`),
        axios.get(`${API}/admin/comments`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setCreators(creatorsRes.data);
      setComments(commentsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async (period) => {
    try {
      const res = await axios.get(`${API}/admin/analytics?period=${period}`, { withCredentials: true });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && (!user || !user.is_admin)) {
      navigate('/');
      return;
    }
    if (user?.is_admin) {
      fetchData();
      fetchAnalytics('7d');
    }
  }, [user, authLoading, navigate, fetchData, fetchAnalytics]);

  const handlePeriodChange = (value) => {
    setAnalyticsPeriod(value);
    fetchAnalytics(value);
  };

  const fetchCreatorAnalytics = async (creatorId) => {
    try {
      const res = await axios.get(`${API}/admin/analytics/creator/${creatorId}?period=${analyticsPeriod}`, { withCredentials: true });
      setSelectedCreatorAnalytics(res.data);
      setIsCreatorAnalyticsOpen(true);
    } catch (error) {
      console.error('Error fetching creator analytics:', error);
      toast.error('Erro ao carregar analytics do criador');
    }
  };

  const handleCreatorPeriodChange = async (value) => {
    if (selectedCreatorAnalytics) {
      try {
        const res = await axios.get(`${API}/admin/analytics/creator/${selectedCreatorAnalytics.creator_id}?period=${value}`, { withCredentials: true });
        setSelectedCreatorAnalytics(res.data);
      } catch (error) {
        console.error('Error fetching creator analytics:', error);
      }
    }
  };

  const filteredCreatorsForAnalytics = creators.filter(creator =>
    creator.name.toLowerCase().includes(creatorSearch.toLowerCase())
  );

  const handleUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = `${window.location.origin}${response.data.url}`;
      setCreatorForm(prev => ({ ...prev, [field]: imageUrl }));
      toast.success('Imagem enviada!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveCreator = async () => {
    if (!creatorForm.name || !creatorForm.bio || !creatorForm.affiliate_link) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingCreator) {
        await axios.put(`${API}/creators/${editingCreator.creator_id}`, creatorForm, {
          withCredentials: true
        });
        toast.success('Criador atualizado!');
      } else {
        await axios.post(`${API}/creators`, creatorForm, {
          withCredentials: true
        });
        toast.success('Criador adicionado!');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erro ao salvar criador');
    }
  };

  const handleDeleteCreator = async (creatorId) => {
    if (!window.confirm('Tem certeza que deseja excluir este criador?')) return;

    try {
      await axios.delete(`${API}/creators/${creatorId}`, { withCredentials: true });
      toast.success('Criador excluído!');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erro ao excluir criador');
    }
  };

  const handleApproveComment = async (commentId) => {
    try {
      await axios.put(`${API}/admin/comments/${commentId}`, 
        { is_approved: true },
        { withCredentials: true }
      );
      toast.success('Comentário aprovado!');
      fetchData();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Erro ao aprovar comentário');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/admin/comments/${commentId}`, { withCredentials: true });
      toast.success('Comentário excluído!');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erro ao excluir comentário');
    }
  };

  const openEditDialog = (creator) => {
    setEditingCreator(creator);
    setCreatorForm({
      name: creator.name,
      bio: creator.bio,
      affiliate_link: creator.affiliate_link,
      profile_image: creator.profile_image || '',
      cover_image: creator.cover_image || '',
      is_featured: creator.is_featured,
      is_top_month: creator.is_top_month,
      photos_count: creator.photos_count || 0,
      videos_count: creator.videos_count || 0,
      price: creator.price || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCreator(null);
    setCreatorForm({
      name: '',
      bio: '',
      affiliate_link: '',
      profile_image: '',
      cover_image: '',
      is_featured: false,
      is_top_month: false,
      photos_count: 0,
      videos_count: 0,
      price: ''
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="w-12 h-12 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return null;
  }

  const pendingComments = comments.filter(c => !c.is_approved);

  return (
    <div className="min-h-screen bg-[#F8FAFC] pt-20 pb-16" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-8">
          Painel Administrativo
        </h1>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                  <Users className="h-5 w-5 text-[#FF5A00]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_creators}</p>
                  <p className="text-sm text-slate-500">Criadores</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-[#FF5A00]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                  <p className="text-sm text-slate-500">Usuários</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                  <Star className="h-5 w-5 text-[#FF5A00]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_ratings}</p>
                  <p className="text-sm text-slate-500">Avaliações</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FFF0E5] rounded-full flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-[#FF5A00]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.pending_comments}</p>
                  <p className="text-sm text-slate-500">Pendentes</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="analytics" className="bg-white rounded-2xl border border-slate-200 p-6">
          <TabsList className="mb-6">
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <TrendingUp className="h-4 w-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="creators" data-testid="tab-creators">Criadores</TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">
              Comentários
              {pendingComments.length > 0 && (
                <span className="ml-2 bg-[#FF5A00] text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingComments.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Usuarios ({users.length})</TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              {/* Period Filter */}
              <div className="flex items-center justify-between">
                <h2 className="font-outfit text-xl font-bold text-slate-900">
                  Visualizações
                </h2>
                <Select value={analyticsPeriod} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="w-40">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Hoje</SelectItem>
                    <SelectItem value="7d">Últimos 7 dias</SelectItem>
                    <SelectItem value="30d">Últimos 30 dias</SelectItem>
                    <SelectItem value="all">Todo período</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-[#FF5A00] to-[#FF8C00] rounded-2xl p-6 text-white">
                      <div className="flex items-center gap-3">
                        <Eye className="h-8 w-8 opacity-80" />
                        <div>
                          <p className="text-3xl font-bold">{analytics.total_views.toLocaleString()}</p>
                          <p className="text-white/80">Total de Views</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded-2xl p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-[#FF5A00]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{analytics.total_home_views.toLocaleString()}</p>
                          <p className="text-slate-500">Views na Home</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-100 rounded-2xl p-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                          <Users className="h-5 w-5 text-[#FF5A00]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{analytics.total_creator_views.toLocaleString()}</p>
                          <p className="text-slate-500">Views em Perfis</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="bg-slate-50 rounded-2xl p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Views por Dia</h3>
                    <div className="flex items-end gap-1 h-40">
                      {analytics.views_by_day.map((day) => {
                        const maxViews = Math.max(...analytics.views_by_day.map(d => d.views), 1);
                        const height = (day.views / maxViews) * 100;
                        return (
                          <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                            <div 
                              className="w-full bg-[#FF5A00] rounded-t-md transition-all hover:bg-[#FF8C00]"
                              style={{ height: `${Math.max(height, 4)}%` }}
                              title={`${day.views} views`}
                            />
                            <span className="text-xs text-slate-400 -rotate-45 origin-left whitespace-nowrap">
                              {day.date}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Top Creators */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900">Top Criadores por Views</h3>
                    </div>
                    
                    {/* Search Creator */}
                    <div className="mb-4">
                      <Input
                        placeholder="Buscar criador específico..."
                        value={creatorSearch}
                        onChange={(e) => setCreatorSearch(e.target.value)}
                        className="max-w-sm"
                        data-testid="creator-search-input"
                      />
                    </div>

                    {/* Creator List */}
                    {(creatorSearch ? filteredCreatorsForAnalytics : analytics.views_by_creator).length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {(creatorSearch ? filteredCreatorsForAnalytics : analytics.views_by_creator).map((creator, index) => (
                          <div 
                            key={creator.creator_id}
                            className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => fetchCreatorAnalytics(creator.creator_id)}
                            data-testid={`analytics-creator-${creator.creator_id}`}
                          >
                            {!creatorSearch && (
                              <span className="w-6 h-6 bg-[#FF5A00] text-white text-sm font-bold rounded-full flex items-center justify-center">
                                {index + 1}
                              </span>
                            )}
                            <img 
                              src={creator.profile_image || 'https://via.placeholder.com/40'}
                              alt={creator.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{creator.name}</p>
                              <p className="text-xs text-slate-400">Clique para ver detalhes</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#FF5A00]">{(creator.views || 0).toLocaleString()}</p>
                              <p className="text-xs text-slate-400">views</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-8">
                        {creatorSearch ? 'Nenhum criador encontrado.' : 'Nenhuma visualização no período selecionado.'}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Creators Tab */}
          <TabsContent value="creators">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-outfit text-xl font-bold text-slate-900">
                Gerenciar Criadores
              </h2>
              <Dialog open={isDialogOpen} onOpenChange={(open) => {
                setIsDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <button 
                    className="bg-[#FF5A00] text-white font-semibold px-4 py-2 rounded-full hover:bg-[#E04F00] transition-colors flex items-center gap-2"
                    data-testid="add-creator-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Adicionar Criador
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-outfit">
                      {editingCreator ? 'Editar Criador' : 'Novo Criador'}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Nome *</label>
                      <Input
                        value={creatorForm.name}
                        onChange={(e) => setCreatorForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Nome do criador"
                        data-testid="creator-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-slate-700">Bio *</label>
                      <Textarea
                        value={creatorForm.bio}
                        onChange={(e) => setCreatorForm(prev => ({ ...prev, bio: e.target.value }))}
                        placeholder="Descrição do criador"
                        rows={3}
                        data-testid="creator-bio-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-slate-700">Link de Afiliado *</label>
                      <Input
                        value={creatorForm.affiliate_link}
                        onChange={(e) => setCreatorForm(prev => ({ ...prev, affiliate_link: e.target.value }))}
                        placeholder="https://service.topshare.com.br/linkearn/api/v1/ref/XXXX"
                        data-testid="creator-link-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Foto de Perfil</label>
                        <div className="mt-1 flex items-center gap-2">
                          <Input
                            value={creatorForm.profile_image}
                            onChange={(e) => setCreatorForm(prev => ({ ...prev, profile_image: e.target.value }))}
                            placeholder="URL da imagem ou faça upload"
                            className="flex-1"
                          />
                          <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 p-2 rounded-lg transition-colors">
                            <Upload className="h-5 w-5 text-slate-600" />
                            <input 
                              type="file" 
                              className="hidden" 
                              accept="image/*"
                              onChange={(e) => handleUpload(e, 'profile_image')}
                              disabled={uploading}
                            />
                          </label>
                        </div>
                        {creatorForm.profile_image && (
                          <img src={creatorForm.profile_image} alt="Preview" className="mt-2 w-20 h-20 object-cover rounded-lg" />
                        )}
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-700">Imagem de Capa</label>
                        <div className="mt-1 flex items-center gap-2">
                          <Input
                            value={creatorForm.cover_image}
                            onChange={(e) => setCreatorForm(prev => ({ ...prev, cover_image: e.target.value }))}
                            placeholder="URL da imagem ou faça upload"
                            className="flex-1"
                          />
                          <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 p-2 rounded-lg transition-colors">
                            <Upload className="h-5 w-5 text-slate-600" />
                            <input 
                              type="file" 
                              className="hidden" 
                              accept="image/*"
                              onChange={(e) => handleUpload(e, 'cover_image')}
                              disabled={uploading}
                            />
                          </label>
                        </div>
                        {creatorForm.cover_image && (
                          <img src={creatorForm.cover_image} alt="Preview" className="mt-2 w-full h-20 object-cover rounded-lg" />
                        )}
                      </div>
                    </div>

                    {/* Content Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700">Qtd. Fotos</label>
                        <Input
                          type="number"
                          min="0"
                          value={creatorForm.photos_count}
                          onChange={(e) => setCreatorForm(prev => ({ ...prev, photos_count: parseInt(e.target.value) || 0 }))}
                          placeholder="0"
                          data-testid="creator-photos-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Qtd. Vídeos</label>
                        <Input
                          type="number"
                          min="0"
                          value={creatorForm.videos_count}
                          onChange={(e) => setCreatorForm(prev => ({ ...prev, videos_count: parseInt(e.target.value) || 0 }))}
                          placeholder="0"
                          data-testid="creator-videos-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">Preço</label>
                        <Input
                          value={creatorForm.price}
                          onChange={(e) => setCreatorForm(prev => ({ ...prev, price: e.target.value }))}
                          placeholder="R$ 29,90/mês"
                          data-testid="creator-price-input"
                        />
                      </div>
                    </div>

                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                          checked={creatorForm.is_featured}
                          onCheckedChange={(checked) => setCreatorForm(prev => ({ ...prev, is_featured: checked }))}
                        />
                        <span className="text-sm text-slate-700">Destaque na Home</span>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Switch
                          checked={creatorForm.is_top_month}
                          onCheckedChange={(checked) => setCreatorForm(prev => ({ ...prev, is_top_month: checked }))}
                        />
                        <span className="text-sm text-slate-700">Top do Mês</span>
                      </label>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        onClick={() => setIsDialogOpen(false)}
                        className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSaveCreator}
                        disabled={uploading}
                        className="bg-[#FF5A00] text-white font-semibold px-6 py-2 rounded-full hover:bg-[#E04F00] transition-colors flex items-center gap-2 disabled:opacity-50"
                        data-testid="save-creator-btn"
                      >
                        <Save className="h-4 w-4" />
                        Salvar
                      </button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Creators List */}
            <div className="space-y-4">
              {creators.length > 0 ? (
                creators.map((creator) => (
                  <div 
                    key={creator.creator_id}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                    data-testid={`admin-creator-${creator.creator_id}`}
                  >
                    <img 
                      src={creator.profile_image || 'https://via.placeholder.com/60'}
                      alt={creator.name}
                      className="w-14 h-14 rounded-xl object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{creator.name}</h3>
                      <p className="text-sm text-slate-500 truncate">{creator.bio}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-400">
                          {creator.views} views • {creator.rating_count} avaliações
                        </span>
                        {creator.is_featured && (
                          <span className="text-xs bg-[#FF5A00] text-white px-2 py-0.5 rounded-full">Destaque</span>
                        )}
                        {creator.is_top_month && (
                          <span className="text-xs bg-[#FFB800] text-white px-2 py-0.5 rounded-full">Top Mês</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditDialog(creator)}
                        className="p-2 text-slate-600 hover:text-[#FF5A00] hover:bg-white rounded-lg transition-colors"
                        data-testid={`edit-creator-${creator.creator_id}`}
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCreator(creator.creator_id)}
                        className="p-2 text-slate-600 hover:text-red-500 hover:bg-white rounded-lg transition-colors"
                        data-testid={`delete-creator-${creator.creator_id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">
                  Nenhum criador cadastrado ainda.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Comments Tab */}
          <TabsContent value="comments">
            <h2 className="font-outfit text-xl font-bold text-slate-900 mb-6">
              Moderar Comentários
            </h2>

            <div className="space-y-4">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div 
                    key={comment.comment_id}
                    className={`p-4 rounded-xl border ${
                      comment.is_approved ? 'bg-white border-slate-200' : 'bg-amber-50 border-amber-200'
                    }`}
                    data-testid={`admin-comment-${comment.comment_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <img 
                        src={comment.user_picture || 'https://via.placeholder.com/40'}
                        alt={comment.user_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{comment.user_name}</span>
                          <span className="text-xs text-slate-400">
                            {new Date(comment.created_at).toLocaleDateString('pt-BR')}
                          </span>
                          {!comment.is_approved && (
                            <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                              Pendente
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 mt-1">{comment.content}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Criador: {creators.find(c => c.creator_id === comment.creator_id)?.name || 'Desconhecido'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!comment.is_approved && (
                          <button
                            onClick={() => handleApproveComment(comment.comment_id)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            data-testid={`approve-comment-${comment.comment_id}`}
                          >
                            <Check className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-comment-${comment.comment_id}`}
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">
                  Nenhum comentário para moderar.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              <h3 className="font-outfit text-xl font-bold text-slate-900">
                Usuarios Cadastrados ({users.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="py-3 px-4 text-sm font-semibold text-slate-600">Foto</th>
                      <th className="py-3 px-4 text-sm font-semibold text-slate-600">Nome</th>
                      <th className="py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
                      <th className="py-3 px-4 text-sm font-semibold text-slate-600">Admin</th>
                      <th className="py-3 px-4 text-sm font-semibold text-slate-600">Data de Cadastro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                        <td className="py-3 px-4">
                          <img
                            src={u.picture || '/favicon.svg'}
                            alt={u.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-slate-900">{u.name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{u.email}</td>
                        <td className="py-3 px-4">
                          {u.is_admin ? (
                            <span className="text-xs bg-[#FF5A00] text-white px-2 py-0.5 rounded-full">Admin</span>
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length === 0 && (
                <p className="text-center text-slate-500 py-8">Nenhum usuario cadastrado.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Creator Analytics Modal */}
      <Dialog open={isCreatorAnalyticsOpen} onOpenChange={setIsCreatorAnalyticsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-3">
              {selectedCreatorAnalytics && (
                <>
                  <img 
                    src={selectedCreatorAnalytics.profile_image || 'https://via.placeholder.com/40'}
                    alt={selectedCreatorAnalytics.name}
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                  Analytics - {selectedCreatorAnalytics.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCreatorAnalytics && (
            <div className="space-y-6 mt-4">
              {/* Period Filter */}
              <div className="flex justify-end">
                <Select defaultValue={analyticsPeriod} onValueChange={handleCreatorPeriodChange}>
                  <SelectTrigger className="w-40">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Hoje</SelectItem>
                    <SelectItem value="7d">Últimos 7 dias</SelectItem>
                    <SelectItem value="30d">Últimos 30 dias</SelectItem>
                    <SelectItem value="all">Todo período</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-[#FF5A00] to-[#FF8C00] rounded-2xl p-5 text-white">
                  <p className="text-3xl font-bold">{selectedCreatorAnalytics.total_views.toLocaleString()}</p>
                  <p className="text-white/80 text-sm">Views no período</p>
                </div>
                <div className="bg-slate-100 rounded-2xl p-5">
                  <p className="text-3xl font-bold text-slate-900">{selectedCreatorAnalytics.total_views_all_time.toLocaleString()}</p>
                  <p className="text-slate-500 text-sm">Views totais (histórico)</p>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-slate-50 rounded-2xl p-5">
                <h3 className="font-semibold text-slate-900 mb-4">Views por Dia</h3>
                <div className="flex items-end gap-1 h-32">
                  {selectedCreatorAnalytics.views_by_day.map((day) => {
                    const maxViews = Math.max(...selectedCreatorAnalytics.views_by_day.map(d => d.views), 1);
                    const height = (day.views / maxViews) * 100;
                    return (
                      <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                        <span className="text-xs text-slate-600 font-medium">{day.views}</span>
                        <div 
                          className="w-full bg-[#FF5A00] rounded-t-md transition-all hover:bg-[#FF8C00]"
                          style={{ height: `${Math.max(height, 4)}%`, minHeight: '4px' }}
                        />
                        <span className="text-xs text-slate-400 -rotate-45 origin-left whitespace-nowrap">
                          {day.date}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
